<template>
  <div
    class="overflow-x-auto"
    :style="tableWrapperStyle"
  >
    <table class="el-table" style="width: 100%">
      <thead :class="theadClass">
        <tr>
          <th
            v-for="column in columns"
            :key="column.key"
            scope="col"
            :class="headerCellClass(column)"
            :style="columnStyle(column)"
            :aria-sort="isSortable(column) ? ariaSortForColumn(column) : undefined"
          >
            <button
              v-if="isSortable(column)"
              type="button"
              class="sort-button"
              @click="handleHeaderClick(column)"
            >
              <slot
                :name="`header-${column.key}`"
              >
                {{ column.label }}
              </slot>
            </button>
            <slot
              v-else
              :name="`header-${column.key}`"
            >
              {{ column.label }}
            </slot>
          </th>
        </tr>
      </thead>
      <tbody v-if="hasRows">
        <tr
          v-for="row in rows"
          :key="getRowKey(row)"
          :class="rowClass"
          @click="onRowClick(row)"
        >
          <td
            v-for="column in columns"
            :key="`${column.key}-${getRowKey(row)}-${row[column.key]}`"
            :class="bodyCellClass(column)"
          >
            <span class="cell">
              <slot
                v-if="$scopedSlots[`cell-${column.key}`]"
                :name="`cell-${column.key}`"
                :row="row"
              />
              <template v-else>
                {{ row[column.key] }}
              </template>
            </span>
          </td>
        </tr>
      </tbody>
      <tbody v-else>
        <tr class="no-data-row">
          <td class="is-center" :colspan="columns.length">
            <span class="cell text-center">
              {{ noDataLabel }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
export default {
  name: 'BaseAccessibleTable',
  props: {
    rows: {
      type: Array,
      default: () => []
    },
    columns: {
      type: Array,
      default: () => []
    },
    noDataLabel: {
      type: String,
      default: 'Sin datos'
    },
    enableScroll: {
      type: Boolean,
      default: false
    },
    maxBodyHeight: {
      type: [Number, String],
      default: 350
    },
    rowKey: {
      type: [String, Function],
      default: 'id'
    },
    sortKey: {
      type: String,
      default: null
    },
    sortOrder: {
      type: String,
      default: null
    },
    theadClass: {
      type: String,
      default: ''
    }
  },
  computed: {
    hasRows () {
      return Array.isArray(this.rows) && this.rows.length > 0
    },
    hasRowClickListener () {
      return !!this.$listeners['row-click']
    },
    rowClass () {
      return this.hasRowClickListener ? 'row-clickable' : ''
    },
    tableWrapperStyle () {
      if (!this.enableScroll) {
        return null
      }
      const height = typeof this.maxBodyHeight === 'number'
        ? `${this.maxBodyHeight}px`
        : this.maxBodyHeight
      return {
        maxHeight: height,
        overflowY: 'auto'
      }
    }
  },
  methods: {
    getRowKey (row) {
      if (typeof this.rowKey === 'function') {
        return this.rowKey(row)
      }
      const key = this.rowKey
      return row[key] || row.id || row.idAdjunto || row.CIF || row.label
    },
    columnStyle (column) {
      const styles = []
      if (column.minWidth) {
        styles.push(`min-width: ${column.minWidth}`)
      }
      if (column.width) {
        styles.push(`width: ${column.width}`)
      }
      if (column.align) {
        styles.push(`text-align: ${column.align}`)
      }
      return styles.join('; ')
    },
    headerCellClass (column) {
      return this.alignClass(column.align)
    },
    bodyCellClass (column) {
      return this.alignClass(column.align)
    },
    alignClass (align) {
      if (align === 'left') return 'is-left'
      if (align === 'right') return 'is-right'
      return 'is-center'
    },
    isSortable (column) {
      return column.sortable === true || (!!this.sortKey && column.key === this.sortKey)
    },
    ariaSortForColumn (column) {
      if (this.sortKey && column.key === this.sortKey) {
        return this.sortOrder || 'none'
      }
      return 'none'
    },
    handleHeaderClick (column) {
      if (!this.isSortable(column)) return
      this.$emit('sort-click', { key: column.key })
    },
    onRowClick (row) {
      if (this.hasRowClickListener) {
        this.$emit('row-click', row)
      }
    }
  }
}
</script>

<style scoped>
table.el-table tbody tr:hover {
  background-color: var(--primary-50);
}

.dark table.el-table tbody tr:hover {
  background-color: var(--darkblue-DEFAULT);
}

table.el-table td.is-left, table.el-table th.is-left {
  text-align: left;
  justify-content: left;
}

table.el-table td.is-center, table.el-table th.is-center {
  text-align: center;
  justify-content: center;
}

table.el-table td.is-right, table.el-table th.is-right {
  text-align: right;
  justify-content: right;
}

table.el-table .cell {
  padding: 0;
}

.row-clickable td {
  cursor: pointer;
}

.sort-button {
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  width: 100%;
  text-align: inherit;
  display: inline-flex;
  align-items: center;
}

.sort-button:focus {
  outline: 2px solid currentColor;
  outline-offset: 2px;
  border-radius: 5px;
}

table.el-table tbody tr td {
  padding: 8px!important;
}

table.el-table tr.no-data-row td {
  cursor: default;
}

table.el-table tbody tr.no-data-row:hover {
  background-color: transparent;
}
</style>
